import React, { useState, useEffect, useRef } from 'react';
import { Bell, ShoppingCart, FileText, Check, CheckCheck, X, BellRing } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../ui/sonner';
import pushService from '../../services/pushNotifications';

import { API_URL, BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const AdminNotifications = ({ onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const dropdownRef = useRef(null);
  const intervalRef = useRef(null);
  const previousUnreadRef = useRef(0);

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API}/admin/notifications`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        
        // Check for new notifications and show push notification
        const newUnread = data.unread_count || 0;
        if (previousUnreadRef.current > 0 && newUnread > previousUnreadRef.current && pushEnabled) {
          const newNotifs = data.notifications?.filter(n => !n.read) || [];
          if (newNotifs.length > 0) {
            const latestNotif = newNotifs[0];
            if (latestNotif.type === 'order') {
              pushService.showNotification('🛒 Nouvelle commande', {
                body: latestNotif.message,
                tag: `order-${latestNotif.id}`,
                data: { url: '/admin' }
              });
            } else if (latestNotif.type === 'quote') {
              pushService.showNotification('📋 Demande de devis', {
                body: latestNotif.message,
                tag: `quote-${latestNotif.id}`,
                data: { url: '/admin' }
              });
            }
          }
        }
        
        // Also show in-app toast for new notifications
        if (previousUnreadRef.current > 0 && newUnread > previousUnreadRef.current) {
          const diff = newUnread - previousUnreadRef.current;
          toast.info(`${diff} nouvelle(s) notification(s)`);
        }
        
        previousUnreadRef.current = newUnread;
        setNotifications(data.notifications || []);
        setUnreadCount(newUnread);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // Request push permission on mount
  useEffect(() => {
    const checkPushPermission = async () => {
      if (pushService.isPermissionGranted()) {
        setPushEnabled(true);
      }
    };
    checkPushPermission();
  }, []);

  const enablePushNotifications = async () => {
    const granted = await pushService.requestPermission();
    if (granted) {
      setPushEnabled(true);
      toast.success('Notifications activées');
    } else {
      toast.error('Permission refusée - Activez les notifications dans les paramètres de votre navigateur');
    }
  };

  // Fetch on mount and every 30 seconds
  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 30000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/admin/notifications/${notificationId}/read`, {
        method: 'POST',
        credentials: 'include'
      });
      
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/admin/notifications/read-all`, {
        method: 'POST',
        credentials: 'include'
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      console.log('Success:', 'Toutes les notifications marquées comme lues');
    } catch (error) {
      console.log('Error:', 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Navigate to the right tab
    if (notification.type === 'order') {
      onNavigate?.('orders');
    } else if (notification.type === 'quote') {
      onNavigate?.('quotes');
    }
    
    setIsOpen(false);
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    return date.toLocaleDateString('fr-FR');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order':
        return <ShoppingCart className="w-4 h-4 text-green-400" />;
      case 'quote':
        return <FileText className="w-4 h-4 text-purple-400" />;
      default:
        return <Bell className="w-4 h-4 text-blue-400" />;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button - Styled as a card */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
          unreadCount > 0 
            ? 'bg-fgs-primary/20 border border-fgs-primary/30 hover:bg-fgs-primary/30' 
            : 'bg-fgs-bg border border-fgs-border hover:bg-fgs-surface-highlight'
        }`}
        data-testid="admin-notifications-btn"
      >
        <div className="relative">
          <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-fgs-primary' : 'text-fgs-text-muted'}`} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        <div className="flex-1">
          <span className={`text-sm font-medium ${unreadCount > 0 ? 'text-white' : 'text-fgs-text-muted'}`}>
            Notifications
          </span>
          {unreadCount > 0 && (
            <p className="text-xs text-fgs-primary">{unreadCount} non lue{unreadCount > 1 ? 's' : ''}</p>
          )}
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-96 bg-fgs-surface border border-fgs-border rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-fgs-border bg-fgs-bg">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-fgs-primary" />
              <span className="font-medium text-white text-sm">Notifications</span>
              {unreadCount > 0 && (
                <span className="bg-red-500/20 text-red-400 text-xs px-2 py-0.5 rounded-full">
                  {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  disabled={loading}
                  className="text-xs text-fgs-primary hover:text-fgs-primary-hover transition-colors flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" />
                  Tout lu
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-fgs-bg rounded ml-2"
              >
                <X className="w-4 h-4 text-fgs-text-muted" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length > 0 ? (
              <div className="divide-y divide-fgs-border">
                {notifications.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full text-left p-4 hover:bg-fgs-bg transition-colors ${
                      !notification.read ? 'bg-fgs-primary/5' : ''
                    }`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        notification.type === 'order' ? 'bg-green-500/20' : 'bg-purple-500/20'
                      }`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium text-sm ${
                            !notification.read ? 'text-white' : 'text-fgs-text-muted'
                          }`}>
                            {notification.title}
                          </span>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-fgs-primary rounded-full flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-fgs-text-muted truncate mt-0.5">
                          {notification.message}
                        </p>
                        <p className="text-xs text-fgs-text-muted/60 mt-1">
                          {formatTimeAgo(notification.created_at)}
                        </p>
                      </div>
                      
                      {notification.read && (
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Bell className="w-8 h-8 text-fgs-text-muted/30 mx-auto mb-2" />
                <p className="text-fgs-text-muted text-sm">Aucune notification</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-fgs-border bg-fgs-bg/50">
              <div className="flex items-center justify-between">
                <p className="text-xs text-fgs-text-muted">
                  Notifications des 7 derniers jours
                </p>
                {!pushEnabled && pushService.supported && (
                  <button
                    onClick={enablePushNotifications}
                    className="flex items-center gap-1 text-xs text-fgs-primary hover:text-fgs-primary-hover"
                  >
                    <BellRing className="w-3 h-3" />
                    Activer alertes
                  </button>
                )}
              </div>
              {pushEnabled && (
                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Alertes navigateur activées
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
